import React, { forwardRef, useCallback, useImperativeHandle } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue, useFieldArray } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { COUNTRIES_LIST, FLIGHT_ITEM_TYPE } from '~/components/Common/CustomOffers/constants';
import DateWidget from '~/components/Common/Elements/DateWidget';

import CustomOfferBusinessTravellerWidget from './CustomOfferBusinessTravellerWidget';

type Props = {
  control: Control<CustomOfferFormValues>;
  register: UseFormRegister<CustomOfferFormValues>;
  errors: FieldErrors<CustomOfferFormValues>;
  setValue: UseFormSetValue<CustomOfferFormValues>;
  isSubmitting: boolean;
  isBusinessTraveller: boolean;
  isEditMode: boolean;
  customerHasPaid: boolean;
  lockVendorPriceFieldsForPaidOrder?: boolean;
  businessEmployees: Array<App.BusinessEmployee>;
  itemType: App.CustomOfferType;
  purchaseType?: 'sales' | 'post-purchase';
  isTravellerCountryRequired?: boolean;
};

export type TravellersSectionRef = {
  addTraveller: (traveller: App.CustomOfferTraveller) => void;
  removeTraveller: (index: number) => void;
  clearAllTravellers: () => void;
  replaceTravellers: (travellers: Array<App.CustomOfferTraveller>) => void;
};

export const TravellersSection = forwardRef<TravellersSectionRef, Props>(
  (
    {
      control,
      register,
      errors,
      setValue,
      isSubmitting,
      isBusinessTraveller,
      isEditMode,
      businessEmployees,
      customerHasPaid,
      lockVendorPriceFieldsForPaidOrder,
      itemType,
      purchaseType,
      isTravellerCountryRequired = true,
    },
    ref,
  ) => {
    const {
      fields: travellers,
      append: addTraveller,
      remove: removeTraveller,
      replace: replaceTravellers,
    } = useFieldArray({ control, name: 'travellers' });

    const isFlightType = itemType === FLIGHT_ITEM_TYPE;

    useImperativeHandle(ref, () => ({
      addTraveller: (traveller: App.CustomOfferTraveller) => addTraveller(traveller),
      removeTraveller: (index: number) => removeTraveller(index),
      clearAllTravellers: () => {
        const count = travellers.length;
        for (let i = count - 1; i >= 0; i--) {
          removeTraveller(i);
        }
      },
      replaceTravellers: (nextTravellers: Array<App.CustomOfferTraveller>) => replaceTravellers(nextTravellers),
    }));

    const handleAddNewTraveller = useCallback(
      (type = 'adult') => {
        const newTraveller = isFlightType
          ? ({
              first_name: '',
              middle_name: '',
              last_name: '',
              type: type,
              date_of_birth: null,
              ...(purchaseType === 'post-purchase' ? { airline_fee: 0, le_fee: 0 } : { fare_and_taxes: 0 }),
              ...(isBusinessTraveller && { employee: null }),
            } as App.CustomOfferTraveller)
          : ({
              name: '',
              type: type,
              country: '',
              date_of_birth: null,
              ...(isBusinessTraveller && { employee: null }),
            } as App.CustomOfferTraveller);

        addTraveller(newTraveller);
      },
      [addTraveller, isBusinessTraveller, isFlightType, purchaseType],
    );

    const handleRemoveTraveller = useCallback(
      (index: number) => {
        removeTraveller(index);
      },
      [removeTraveller],
    );

    const getTravellerPricingErrors = (index: number) =>
      errors.travellers?.[index] as FieldErrors<
        Pick<App.CustomOfferTraveller, 'airline_fee' | 'fare_and_taxes' | 'le_fee'>
      >;
    const shouldLockFlightPriceFields = lockVendorPriceFieldsForPaidOrder ?? (isEditMode && customerHasPaid);

    return (
      <>
        {travellers.map((traveller, index) => (
          <Stack
            key={traveller.id}
            direction="column"
            mt={2}
            gap={2}
            padding={2}
            className="booking-form"
            data-no-dark-override
            sx={{ border: 1, borderColor: 'grey.300', mb: 2 }}
          >
            <Box>
              <Typography variant="h6">Traveller details (Per government issued ID)</Typography>
            </Box>
            <Grid mt={2} container columns={6} spacing={2} key={`${traveller.id}-row1`}>
              {isBusinessTraveller ? (
                <CustomOfferBusinessTravellerWidget
                  index={index}
                  register={register}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  setValue={setValue}
                  employees={businessEmployees}
                  control={control}
                  includeMiddleName={isFlightType}
                  isTravellerCountryRequired={isTravellerCountryRequired}
                />
              ) : isFlightType ? (
                <>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <TextField
                      {...register(`travellers.${index}.first_name`)}
                      helperText={errors.travellers?.[index]?.first_name?.message}
                      error={!!errors.travellers?.[index]?.first_name}
                      disabled={isSubmitting}
                      placeholder="First name"
                      required
                      fullWidth
                      data-testid={`traveller-first-name-${index}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <TextField
                      {...register(`travellers.${index}.middle_name`)}
                      helperText={errors.travellers?.[index]?.middle_name?.message}
                      error={!!errors.travellers?.[index]?.middle_name}
                      disabled={isSubmitting}
                      placeholder="Middle name (optional)"
                      fullWidth
                      data-testid={`traveller-middle-name-${index}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <TextField
                      {...register(`travellers.${index}.last_name`)}
                      helperText={errors.travellers?.[index]?.last_name?.message}
                      error={!!errors.travellers?.[index]?.last_name}
                      disabled={isSubmitting}
                      placeholder="Last name"
                      required
                      fullWidth
                      data-testid={`traveller-last-name-${index}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <Controller
                      name={`travellers.${index}.type`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Traveller type"
                          required
                          helperText={error?.message}
                          error={!!error}
                          disabled={isSubmitting}
                          select
                          fullWidth
                          data-testid={`traveller-type-${index}`}
                        >
                          <MenuItem value="adult">Adult</MenuItem>
                          <MenuItem value="child">Child</MenuItem>
                          <MenuItem value="infant">Infant</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <Controller
                      control={control}
                      name={`travellers.${index}.date_of_birth`}
                      render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                        <DateWidget
                          {...field}
                          slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                          disabled={isSubmitting}
                          label="Date of birth"
                          fullWidth
                          data-testid={`traveller-date-of-birth-${index}`}
                        />
                      )}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      {...register(`travellers.${index}.name`)}
                      helperText={errors.travellers?.[index]?.name?.message}
                      error={!!errors.travellers?.[index]?.name}
                      disabled={isSubmitting}
                      placeholder="Traveller name"
                      required
                      fullWidth
                      data-testid={`traveller-name-${index}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <Controller
                      name={`travellers.${index}.country`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Country of residence"
                          required={isTravellerCountryRequired}
                          helperText={error?.message}
                          error={!!error}
                          disabled={isSubmitting}
                          select
                          fullWidth
                          data-testid={`traveller-country-${index}`}
                        >
                          {COUNTRIES_LIST.map((country) => (
                            <MenuItem value={country} key={country}>
                              {country}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <Controller
                      control={control}
                      name={`travellers.${index}.date_of_birth`}
                      render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                        <DateWidget
                          {...field}
                          slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                          disabled={isSubmitting}
                          label="Date of birth"
                          fullWidth
                          data-testid={`traveller-date-of-birth-${index}`}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1 }}>
                    <Controller
                      name={`travellers.${index}.type`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Traveller type"
                          required
                          helperText={error?.message}
                          error={!!error}
                          disabled={isSubmitting}
                          select
                          fullWidth
                          data-testid={`traveller-type-${index}`}
                        >
                          <MenuItem value="adult">Adult</MenuItem>
                          <MenuItem value="child">Child</MenuItem>
                          <MenuItem value="infant">Infant</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            {isFlightType && (
              <>
                <Typography variant="h6">Price details</Typography>

                <Grid container spacing={2}>
                  {purchaseType === 'post-purchase' ? (
                    <>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          {...register(`travellers.${index}.airline_fee`)}
                          helperText={getTravellerPricingErrors(index)?.airline_fee?.message}
                          error={!!getTravellerPricingErrors(index)?.airline_fee}
                          disabled={isSubmitting || shouldLockFlightPriceFields}
                          label="Airline fee"
                          required
                          type="number"
                          slotProps={{
                            htmlInput: {
                              step: 0.01,
                            },
                          }}
                          fullWidth
                          data-testid={`traveller-airline-fee-${index}`}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          {...register(`travellers.${index}.le_fee`)}
                          helperText={getTravellerPricingErrors(index)?.le_fee?.message}
                          error={!!getTravellerPricingErrors(index)?.le_fee}
                          disabled={isSubmitting || shouldLockFlightPriceFields}
                          label="LE fee"
                          required
                          type="number"
                          slotProps={{
                            htmlInput: {
                              step: 0.01,
                            },
                          }}
                          fullWidth
                          data-testid={`traveller-le-fee-${index}`}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        {...register(`travellers.${index}.fare_and_taxes`)}
                        helperText={errors.travellers?.[index]?.fare_and_taxes?.message}
                        error={!!errors.travellers?.[index]?.fare_and_taxes}
                        disabled={isSubmitting || shouldLockFlightPriceFields}
                        label="Fare and taxes"
                        required
                        type="number"
                        slotProps={{
                          htmlInput: {
                            step: 0.01,
                          },
                        }}
                        fullWidth
                        data-testid={`traveller-fare-and-taxes-${index}`}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }}></Grid>
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 6, md: 1 }}></Grid>
              {travellers.length !== 1 && !isSubmitting && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="text"
                    size="small"
                    title="Remove traveller"
                    color="error"
                    data-testid="remove-traveller-btn"
                    onClick={() => handleRemoveTraveller(index)}
                    startIcon={<DeleteIcon />}
                  >
                    Remove Traveller
                  </Button>
                </Box>
              )}
            </Grid>
          </Stack>
        ))}
        {!isSubmitting && (
          <Stack direction="row" justifyContent="space-between">
            <Button
              color="primary"
              title="Add traveller"
              data-testid="add-traveller-btn"
              variant="text"
              size="small"
              onClick={() => handleAddNewTraveller()}
              classes={{
                root: 'T-quantity-plus-traveller', // Required for e2e test
              }}
              startIcon={<AddIcon />}
            >
              Add Traveller
            </Button>
          </Stack>
        )}
      </>
    );
  },
);

TravellersSection.displayName = 'TravellersSection';
